// MainPage/components/RewardActivityTicker/RewardActivityTicker.tsx

import "./RewardActivityTicker.scss";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";
import GoldIcon from "../../../../../assets/icons/gold-icon.svg?react";
import WldIcon from "../../../../../assets/icons/wld-icon.svg?react";
import UsdcIconFat from "../../../../../assets/icons/usdc-icon-fat.svg?react";
import { useSlice } from "@bridge-applications/provider-kit";
import { ApiGlobalRewardActivityResSchema } from "@bridge-applications/eh-types";
import type { z } from "zod";
import {
    walletToDarkUsernameBackground,
    walletToGradient,
} from "../../../../../logic/helpers/walletToGradient";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type GlobalRewardItem = z.infer<
    typeof ApiGlobalRewardActivityResSchema
>["data"]["items"][number];

const emphasizeLeadingZero = (s: string) => {
    const parts = s.split(".");
    if (parts.length !== 2 || !parts[0] || !parts[1]) return s;

    const integerPart = parts[0];
    const decimalPart = parts[1];

    if (parseInt(integerPart, 10) > 0) {
        return (
            <>
                {integerPart}
                <span className="small">.{decimalPart}</span>
            </>
        );
    }

    const m = s.match(/^0\.(?:0)?/);
    if (!m) return s;
    return (
        <>
            <span className="small">{m[0]}</span>
            {s.slice(m[0].length)}
        </>
    );
};

const formatAmountTwoDecimals = (amountStr: string) => {
    const n = Number(amountStr);
    if (Number.isNaN(n)) return amountStr;
    return n.toFixed(2);
};

const base64offensiveNames = [
    "YXlsZW4=",
    "ZmFnZ290",
    "bmlnZ2Vy",
    "bmVncm9v",
    "bmVncm8=",
    "Y3Vjaw==",
    "ZnVja251dA==",
    "amV3",
    "cmFwZQ==",
    "a2lsbA==",
    "ZGVhdGg=",
    "ZHJ1Zw==",
    "c2x1dA==",
    "bmF6emk=",
];

const decodeBase64 = (value: string): string => {
    if (typeof atob === "function") {
        return atob(value);
    }
    if (typeof Buffer !== "undefined") {
        // fallback for non-browser envs (SSR / node)
        return Buffer.from(value, "base64").toString("utf-8");
    }
    return value;
};

const usernameContainsOffensiveSubstring = (username: string) => {
    const lowerUsername = username.toLowerCase();
    return base64offensiveNames.some((b64) => {
        try {
            const decoded = decodeBase64(b64).toLowerCase().trim();
            if (!decoded) return false;
            return lowerUsername.includes(decoded);
        } catch {
            return false;
        }
    });
};

const shortenWalletAddress = (addr: string) => {
    return `${addr.slice(0, 4)}…${addr.slice(-4)}`;
};

const toDisplayName = (item: GlobalRewardItem) => {
    const u = item.user;
    const walletAddr = u?.wallet_address;
    const walletShort = walletAddr ? shortenWalletAddress(walletAddr) : null;

    if (u?.username) {
        if (usernameContainsOffensiveSubstring(u.username)) {
            if (walletShort) return walletShort;
            console.log("[CENSORED USERNAME]", item);
            return "anon";
        }
        return u.username;
    }
    if (u?.wallet_address) {
        const addr = u.wallet_address;
        return `${addr.slice(0, 4)}…${addr.slice(-4)}`;
    }
    console.log("[ANON]", item);
    return "anon";
};

const toWallet = (item: GlobalRewardItem) =>
    item.user?.wallet_address ?? "0x0000000000000000000000000000000000000000";

// ⬇⬇⬇ make this i18n-aware
const getRelativeTime = (
    iso: string,
    t: ReturnType<typeof useTranslation>["t"],
) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);

    if (diffSec < 60) {
        return t("RewardActivityTicker.relativeSecondsAgo", {
            count: diffSec,
        });
    } else if (diffSec < 60 * 60) {
        const mins = Math.floor(diffSec / 60);
        return t("RewardActivityTicker.relativeMinutesAgo", {
            count: mins,
        });
    } else if (diffSec < 60 * 60 * 24) {
        const hours = Math.floor(diffSec / (60 * 60));
        return t("RewardActivityTicker.relativeHoursAgo", {
            count: hours,
        });
    } else if (diffSec < 60 * 60 * 24 * 30) {
        const days = Math.floor(diffSec / (60 * 60 * 24));
        return t("RewardActivityTicker.relativeDaysAgo", {
            count: days,
        });
    } else if (diffSec < 60 * 60 * 24 * 365) {
        const months = Math.floor(diffSec / (60 * 60 * 24 * 30));
        return t("RewardActivityTicker.relativeMonthsAgo", {
            count: months,
        });
    } else {
        const years = Math.floor(diffSec / (60 * 60 * 24 * 365));
        return t("RewardActivityTicker.relativeYearsAgo", {
            count: years,
        });
    }
};

export default function RewardActivityTicker() {
    const { state: appSlice } = useSlice("appSlice");
    const { t } = useTranslation();
    const [emblaRef] = useEmblaCarousel(
        {
            loop: true,
            dragFree: true,
            align: "center",
            skipSnaps: false,
        },
        [
            AutoScroll({
                playOnInit: true,
                speed: 0.5,
                direction: "forward",
                stopOnInteraction: false,
            }),
        ],
    );

    const items: GlobalRewardItem[] = useMemo(() => {
        if (!appSlice.globalRewardActivity?.items) return [];

        const allItems = [...appSlice.globalRewardActivity.items];
        const referralItems = allItems.filter(
            (item) => item.source === "REFERRAL",
        );
        const surveyItems = allItems.filter((item) => item.source === "SURVEY");

        const result: GlobalRewardItem[] = [];
        const maxLength = Math.max(referralItems.length, surveyItems.length);

        for (let i = 0; i < maxLength; i++) {
            const referralItem = referralItems[i];
            if (referralItem) {
                result.push(referralItem);
            }
            const surveyItem = surveyItems[i];
            if (surveyItem) {
                result.push(surveyItem);
            }
        }

        return result;
    }, [appSlice.globalRewardActivity?.items]);

    const prepared = useMemo(
        () =>
            items.map((it) => ({
                ...it,
                _displayName: toDisplayName(it),
                _wallet: toWallet(it),
            })),
        [items],
    );

    if (!prepared.length) {
        return <div className="reward-activity-ticker empty" aria-hidden />;
    }

    return (
        <div className="reward-activity-ticker" aria-label="Recent activity">
            <div className="embla" ref={emblaRef}>
                <div className="embla__container">
                    {prepared.map((item, idx) => (
                        <div
                            className="embla__slide reward-activity-ticker__chip"
                            key={`${item.occurred_at}-${idx}`}
                        >
                            <div className="reward-activity-ticker__top">
                                <div className="earnings">
                                    {item.unit.toUpperCase() === "USDC" && (
                                        <UsdcIconFat className="unit-icon" />
                                    )}
                                    {item.unit.toUpperCase() === "WLD" && (
                                        <WldIcon className="unit-icon" />
                                    )}
                                    {item.unit.toUpperCase() === "GOLD" && (
                                        <GoldIcon className="unit-icon" />
                                    )}
                                    <span className="amount">
                                        {emphasizeLeadingZero(
                                            formatAmountTwoDecimals(
                                                item.amount,
                                            ),
                                        )}
                                    </span>
                                </div>
                                <div className="divider" aria-hidden />
                                <span className="time-ago">
                                    {getRelativeTime(item.occurred_at, t)}
                                </span>
                            </div>
                            <div
                                className="reward-activity-ticker__bottom"
                                style={{
                                    background: `${walletToDarkUsernameBackground(
                                        item._wallet,
                                    )}`,
                                }}
                            >
                                <div
                                    className="avatar"
                                    style={{
                                        background: `linear-gradient(135deg, ${
                                            walletToGradient(item._wallet)[0]
                                        }, ${
                                            walletToGradient(item._wallet)[1]
                                        })`,
                                    }}
                                    aria-hidden
                                >
                                    <span>
                                        {item._displayName
                                            .slice(0, 1)
                                            .toUpperCase()}
                                    </span>
                                </div>
                                <span className="username">
                                    @{item._displayName}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
